import React, { useEffect, useState } from "react";

import { useUpdatePlaysRecipientsMutation } from "../../redux/slices";
import { AccessUser } from "../../redux/slices/fast-server-api/types";
import { SportPlay } from "../../types";
import { Button } from "../Button/button";
import Checkbox from "../CheckBox/Checkbox";
import { Modal } from "../modal/modal";
import styles from "./mobile-access-dialog.module.scss";

export interface MobileAccessDialogProps<TPlay> {
  open: boolean;
  onClose: () => void;
  accessUsers: AccessUser[];
  plays?: TPlay[];
  playIds?: string[];
}

export const MobileAccessDialog = <TPlay extends SportPlay>({
  open,
  onClose,
  accessUsers,
  plays,
  playIds,
}: MobileAccessDialogProps<TPlay>) => {
  const [updatePlaysRecipients] = useUpdatePlaysRecipientsMutation();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const coaches = accessUsers.filter((user) => user.roleNames.includes("Coach"));
  const players = accessUsers.filter((user) => user.roleNames.includes("Player"));
  const scouts = accessUsers.filter((user) => user.roleNames.includes("Scout"));

  const allCoachesSelected =
    coaches.length > 0 && coaches.every((coach) => selectedUserIds.includes(coach.userIdFormatted));
  const allPlayersSelected =
    players.length > 0 && players.every((player) => selectedUserIds.includes(player.userIdFormatted));
  const allScoutsSelected =
    scouts.length > 0 && scouts.every((scout) => selectedUserIds.includes(scout.userIdFormatted));

  useEffect(() => {
    if (plays) {
      // TODO need a better way of building this list as it only uses plays selected on the current page
      // build list of users present in all plays
      const userIds = plays.reduce((acc, play) => {
        const playUserIds = play.recipients
          .filter((rec) => accessUsers.some((acc) => acc.userIdFormatted == rec.recipientId)) // filter out users not in accessUsers
          .map((recipient) => recipient.recipientId);
        return acc.concat(playUserIds);
      }, [] as string[]);

      // remove duplicates
      const uniqueUserIds = [...new Set(userIds)];

      setSelectedUserIds(uniqueUserIds);
    }
  }, [plays]);

  const handleSave = () => {
    if (playIds) {
      updatePlaysRecipients({
        playIds: playIds,
        recipients: selectedUserIds,
      });
    }

    onClose();
  };

  const handleSelectAll = (role: "Coach" | "Player" | "Scout") => {
    let newSelectedUserIds = [...selectedUserIds];
    if (role === "Coach") {
      if (allCoachesSelected) {
        newSelectedUserIds = newSelectedUserIds.filter(
          (userId) => !coaches.map((coach) => coach.userIdFormatted).includes(userId)
        );
      } else {
        newSelectedUserIds = [
          ...newSelectedUserIds,
          ...coaches
            .filter((role) => !selectedUserIds.includes(role.userIdFormatted))
            .map((coach) => coach.userIdFormatted),
        ];
      }
    } else if (role === "Player") {
      if (allPlayersSelected) {
        newSelectedUserIds = newSelectedUserIds.filter(
          (userId) => !players.map((player) => player.userIdFormatted).includes(userId)
        );
      } else {
        newSelectedUserIds = [
          ...newSelectedUserIds,
          ...players
            .filter((role) => !selectedUserIds.includes(role.userIdFormatted))
            .map((player) => player.userIdFormatted),
        ];
      }
    } else if (role === "Scout") {
      if (allScoutsSelected) {
        newSelectedUserIds = newSelectedUserIds.filter(
          (userId) => !scouts.map((scout) => scout.userIdFormatted).includes(userId)
        );
      } else {
        newSelectedUserIds = [
          ...newSelectedUserIds,
          ...scouts
            .filter((role) => !selectedUserIds.includes(role.userIdFormatted))
            .map((scout) => scout.userIdFormatted),
        ];
      }
    }
    setSelectedUserIds(newSelectedUserIds);
  };

  const handleSelectUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={plays && plays.length > 1 ? `Mobile Access (${plays?.length} plays)` : "Mobile Access"}
      actions={[
        <Button intent="danger" text="Cancel" minimal={true} key={1} onClick={onClose} />,
        <Button intent="primary" text="Save" key={2} onClick={handleSave} />,
      ]}
    >
      <div className={styles.container}>
        <div className={styles.roleContainer}>
          <UserAccessCheckbox
            name="Coaches"
            onClick={() => handleSelectAll("Coach")}
            nameClass={styles.selectAllRole}
            selected={allCoachesSelected}
          />
          {coaches.map((coach) => (
            <UserAccessCheckbox
              key={coach.id}
              name={`${coach.firstName} ${coach.lastName}`}
              onClick={() => handleSelectUser(coach.userIdFormatted)}
              selected={selectedUserIds.includes(coach.userIdFormatted)}
            />
          ))}
        </div>
        <div className={styles.roleContainer}>
          <UserAccessCheckbox
            name="Scouts"
            onClick={() => handleSelectAll("Scout")}
            nameClass={styles.selectAllRole}
            selected={allScoutsSelected}
          />
          {scouts.map((scout) => (
            <UserAccessCheckbox
              key={scout.id}
              name={`${scout.firstName} ${scout.lastName}`}
              onClick={() => handleSelectUser(scout.userIdFormatted)}
              selected={selectedUserIds.includes(scout.userIdFormatted)}
            />
          ))}
        </div>
        <div className={styles.roleContainer}>
          <UserAccessCheckbox
            name="Players"
            onClick={() => handleSelectAll("Player")}
            nameClass={styles.selectAllRole}
            selected={allPlayersSelected}
          />
          {players.map((player) => (
            <UserAccessCheckbox
              key={player.id}
              name={`${player.firstName} ${player.lastName}`}
              onClick={() => handleSelectUser(player.userIdFormatted)}
              selected={selectedUserIds.includes(player.userIdFormatted)}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

const UserAccessCheckbox = ({
  name,
  onClick,
  nameClass,
  selected,
}: {
  name: string;
  onClick: () => void;
  nameClass?: string;
  selected: boolean;
}) => {
  return (
    <div className={styles.userAccessCheckbox}>
      <Checkbox
        checked={selected}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      />
      <span className={nameClass}>{name}</span>
    </div>
  );
};
